"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, MailCheck } from "lucide-react";
import Link from "next/link";
import { Logo } from "@/components/brand/logo";
import { useForgotPassword } from "@/hooks/api/use-auth";

const forgotPasswordSchema = z.object({
  email: z.email("E-mail inválido"),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  // O backend sempre responde de forma neutra (mesmo se o e-mail não existir),
  // então qualquer resposta (sucesso ou erro) leva à mesma mensagem neutra.
  const forgotPasswordMutation = useForgotPassword({
    onSuccess: () => setSubmitted(true),
    onError: () => setSubmitted(true),
  });

  const onSubmit = (data: ForgotPasswordForm) => {
    forgotPasswordMutation.mutate({ email: data.email });
  };

  return (
    <div className="flex h-screen w-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#0000fe] via-[#0000fe]/95 to-[#3afcf3]/90">
      {/* Decorative Background Elements */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,#3afcf3,transparent_70%)] opacity-40 blur-3xl" />
        <div className="absolute right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,#acfcf3,transparent_70%)] opacity-30 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="overflow-hidden rounded-2xl border border-white/20 bg-white shadow-[0_40px_100px_-50px_rgba(0,0,0,0.7)] backdrop-blur-xl">
          <div className="px-10 py-10">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <Logo width={160} height={53} />
            </div>

            {submitted ? (
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#0000fe]/10">
                  <MailCheck className="h-8 w-8 text-[#0000fe]" />
                </div>
                <h1 className="mb-2 text-2xl font-semibold text-[#000000]">
                  Verifique seu e-mail
                </h1>
                <p className="mb-8 text-sm text-[#464646]">
                  Se o e-mail estiver cadastrado, você receberá instruções para
                  redefinir sua senha em alguns instantes.
                </p>
                <Link href="/login">
                  <Button className="h-12 w-full rounded-2xl bg-gradient-to-r from-[#0000fe] via-[#0000fe] to-[#3afcf3] text-base font-semibold text-white shadow-lg hover:shadow-xl">
                    Voltar para o login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-8 text-center">
                  <h1 className="mb-2 text-2xl font-semibold text-[#000000]">
                    Esqueceu a senha?
                  </h1>
                  <p className="text-sm text-[#464646]">
                    Informe seu e-mail para receber as instruções de
                    redefinição de senha
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="w-full space-y-5"
                >
                  <div className="w-full space-y-2">
                    <Label
                      htmlFor="email"
                      className="pl-11 text-xs font-semibold tracking-wider text-[#464646] uppercase"
                    >
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-[#464646]/60" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        autoComplete="email"
                        className="h-12 w-full rounded-2xl border-[#e5e5e5] bg-white !pr-4 !pl-12 text-sm text-[#000000] placeholder:text-[#464646]/50 focus:border-[#0000fe] focus:ring-2 focus:ring-[#0000fe]/20"
                        {...register("email")}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-error text-xs">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={forgotPasswordMutation.isPending}
                    className="mt-6 h-12 w-full rounded-2xl bg-gradient-to-r from-[#0000fe] via-[#0000fe] to-[#3afcf3] text-base font-semibold text-white shadow-lg hover:shadow-xl"
                  >
                    {forgotPasswordMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                        Enviando...
                      </span>
                    ) : (
                      "Enviar instruções"
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <Link
                    href="/login"
                    className="font-medium text-[#0000fe] hover:opacity-80"
                  >
                    Voltar para o login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-white/70">
          © 2026 Topa+. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
